import { useContext } from 'react';
import { WrappedFieldProps } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import styled, { ThemeContext } from 'styled-components';

import FormError from 'inquiry/components/FormError';
import MaterialSelect from 'inquiry/components/MaterialSelect';
import Select from 'inquiry/components/Select';
import { containerSSTStyles, labelSSTStyles, materialContainerStyles } from 'inquiry/mixins/form';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { InputStyles } from 'lib/interfaces/input-styles';
import { InputSelectBaseStyles } from 'lib/interfaces/ui-components';

// Containers copied from ThemedFormInput.tsx
const OutlinedContainer = styled('div')<{ styles?: InputSelectBaseStyles }>`
  ${({ styles }) => containerSSTStyles(styles)}
  ${({ styles }) => labelSSTStyles(styles)}
`;

const MaterialContainer = styled('div')`
  ${materialContainerStyles}
`;

interface ThemedFormSelectProps extends FormControlProps {
  className?: string;
  id: string;
  label?: string;
  styles?: InputSelectBaseStyles;
}

function ThemedFormSelect(props: ThemedFormSelectProps & WrappedFieldProps) {
  const {
    id,
    input,
    label,
    styles,
    meta: { error, submitFailed },
    ...passThroughProps
  } = props;
  const { inputStyle } = useContext(ThemeContext);
  const hasError = !!(error && submitFailed);

  switch (inputStyle) {
    case InputStyles.Material:
      return (
        <MaterialContainer>
          <MaterialSelect
            {...input}
            {...passThroughProps}
            id={id}
            hasError={hasError}
            aria-invalid={hasError}
            aria-errormessage={`${id}--error`}
            aria-describedby={`${id}--error`}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            labelText={label}
            styles={styles}
          />
          <FormError id={`${id}--error`} hasError={hasError} errorMessage={error} styles={styles} />
        </MaterialContainer>
      );

    default:
    case InputStyles.Outlined:
      return (
        <OutlinedContainer styles={styles}>
          {label && (
            <Markdown<'label'> wrapper='label' wrapperProps={{ htmlFor: id }}>
              {label}
            </Markdown>
          )}
          <Select
            {...input}
            {...passThroughProps}
            id={id}
            hasError={hasError}
            aria-invalid={hasError}
            aria-errormessage={`${id}--error`}
            aria-describedby={`${id}--error`}
            styles={styles}
          />
          <FormError id={`${id}--error`} hasError={hasError} errorMessage={error} styles={styles} />
        </OutlinedContainer>
      );
  }
}

export default ThemedFormSelect;
